import { useState } from "react"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid"

import type { SolarSungageStartStateDto } from "~/api/dto/solar-sungage.dto"
import { invalidateSolarSungageDependents } from "~/api/hooks/solar-sungage-application-cache"
import useSolarSungageApplication from "~/api/hooks/useSolarSungageApplication"
import useStartSolarSungageApplication from "~/api/hooks/useStartSolarSungageApplication"
import { isSolarRefreshPricingRequiredError } from "~/api/solar-proposal-errors"

import {
  REPLACE_SOLAR_SUNGAGE_LABEL,
  SOLAR_SUNGAGE_HOSTED_EYEBROW,
  SOLAR_SUNGAGE_LAST_START_FAILED_LABEL,
  SOLAR_SUNGAGE_NO_APPLICATION_PROMPT,
  SOLAR_SUNGAGE_REPLACES_PROMPT,
  solarSungageCommandErrorMessage,
  solarSungageStatusBundle,
  START_SOLAR_SUNGAGE_BUSY_LABEL,
  START_SOLAR_SUNGAGE_LABEL,
} from "./solar-sungage-bundle"
import { SolarRefreshPricingFix } from "./SolarRefreshPricingFix"
import { useConcertApplicationNotifications } from "../ProposalSetup/financing/concert/use-concert-application-notifications"
import { btnPrimary } from "../ProposalSetup/financing/financing-chrome"
import { SungageApplicationStatus } from "../ProposalSetup/financing/sungage/SungageApplicationStatus"
import { SungageHostedApplication } from "../ProposalSetup/financing/sungage/SungageHostedApplication"

export const SOLAR_SUNGAGE_LOAD_ERROR = `Sungage application status could not be loaded.`

/**
 * Start, or why it waits: the server's gate (a stale Quote with Refresh
 * Pricing inline, an amount Sungage won't finance, a lock), never a client
 * guess.
 */
const StartApplication = ({
  proposalId,
  start,
  replaces,
  onHostedUrl,
}: {
  proposalId: string
  start: SolarSungageStartStateDto
  replaces: boolean
  onHostedUrl: (url: string) => void
}): JSX.Element | null => {
  const command = useStartSolarSungageApplication()
  // One key per Start intent: a retry after a lost answer recovers the same attempt.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID())
  const refreshRequired =
    (command.isError && isSolarRefreshPricingRequiredError(command.error)) ||
    (!start.allowed && start.refreshPricing)

  const onStart = (): void => {
    command.mutate(
      { proposalId, body: { idempotencyKey } },
      {
        onSuccess: (handoff) => {
          setIdempotencyKey(crypto.randomUUID())
          if (handoff.applicationUrl != null) onHostedUrl(handoff.applicationUrl)
        },
      },
    )
  }

  if (!start.allowed && start.reason == null) return null
  return (
    <div
      data-testid="solar-sungage-start"
      className="flex flex-col items-start gap-2"
    >
      {replaces ? (
        <p
          role="status"
          data-testid="solar-sungage-replaces"
          className="text-[length:var(--step--2)] text-bp-gold"
        >
          {SOLAR_SUNGAGE_REPLACES_PROMPT}
        </p>
      ) : null}
      {start.allowed ? (
        <button
          type="button"
          data-testid="solar-sungage-start-button"
          className={`${btnPrimary} inline-flex items-center gap-2`}
          disabled={command.isLoading}
          onClick={onStart}
        >
          {command.isLoading
            ? START_SOLAR_SUNGAGE_BUSY_LABEL
            : replaces
              ? REPLACE_SOLAR_SUNGAGE_LABEL
              : START_SOLAR_SUNGAGE_LABEL}
          <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-4" />
        </button>
      ) : (
        <p
          role="status"
          data-testid="solar-sungage-start-blocked"
          className="text-[length:var(--step--2)] text-bp-gold"
        >
          {start.reason}
        </p>
      )}
      {command.isError ? (
        <p
          role="alert"
          data-testid="solar-sungage-start-error"
          className="text-[length:var(--step--2)] text-bp-danger"
        >
          {solarSungageCommandErrorMessage(command.error)}
        </p>
      ) : null}
      {refreshRequired ? (
        <SolarRefreshPricingFix
          proposalId={proposalId}
          onRefreshed={() => command.reset()}
        />
      ) : null}
    </div>
  )
}

/**
 * Sungage's financing status on a Solar Proposal: the shared Sungage
 * application status (outcome, expiration, submitted and approved terms,
 * activity, Resume into the hosted application), polled every 45 seconds
 * while open and refreshed on the gateway's loan-status notification. Before
 * an application, or once the program changed, Start with its gate.
 */
export const SolarSungageFinancingStatus = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const state = useSolarSungageApplication(proposalId)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  useConcertApplicationNotifications(
    proposalId,
    invalidateSolarSungageDependents,
  )

  if (state.data == null) {
    return (
      <p
        role={state.isError ? `alert` : `status`}
        data-testid="solar-sungage-loading"
        className="text-[length:var(--step--1)] text-bp-text-muted"
      >
        {state.isError
          ? SOLAR_SUNGAGE_LOAD_ERROR
          : `Loading Sungage application…`}
      </p>
    )
  }
  const { application, start, lastStartFailure } = state.data
  // A declined or withdrawn application has no Continue action; Start (the
  // server's gate) is the only way to apply again.
  const applicationEnded =
    application != null &&
    (application.creditStatus === `Ineligible for financing` ||
      application.creditStatus === `verified-withdrawal`)

  return (
    <div
      data-testid="solar-sungage-financing-status"
      className="flex flex-col gap-4"
    >
      {application == null ? (
        <p
          data-testid="solar-sungage-no-application"
          className="text-[length:var(--step--1)] text-bp-text-muted"
        >
          {SOLAR_SUNGAGE_NO_APPLICATION_PROMPT}
        </p>
      ) : null}
      {lastStartFailure != null && application == null ? (
        <p
          role="status"
          data-testid="solar-sungage-last-start-failure"
          className="text-[length:var(--step--2)] text-bp-gold"
        >
          {`${SOLAR_SUNGAGE_LAST_START_FAILED_LABEL}: ${lastStartFailure}`}
        </p>
      ) : null}
      {application == null || applicationEnded || start.replacesCurrent ? (
        <StartApplication
          proposalId={proposalId}
          start={start}
          replaces={application != null && start.replacesCurrent}
          onHostedUrl={setHostedUrl}
        />
      ) : null}
      {application != null ? (
        <SungageApplicationStatus
          key={application.applicationId}
          bundle={solarSungageStatusBundle}
          proposalId={proposalId}
          applicationId={application.applicationId}
          initial={application}
        />
      ) : null}
      {hostedUrl != null ? (
        <SungageHostedApplication
          url={hostedUrl}
          onClose={() => setHostedUrl(null)}
          eyebrow={SOLAR_SUNGAGE_HOSTED_EYEBROW}
          testIdPrefix="solar"
        />
      ) : null}
    </div>
  )
}
